import { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Users, KeyRound, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card, CardHead } from '@/Components/ui/Card';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { Checkbox } from '@/Components/ui/Checkbox';
import { IconButton } from '@/Components/ui/IconButton';
import { PageHeader } from '@/Components/data/PageHeader';
import { EmptyState } from '@/Components/data/EmptyState';
import { StatusBadge } from '@/Components/ui/Badge';
import { TableActions } from '@/Components/ui/TableActions';
import { Field } from '@/Components/forms/Field';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Components/ui/Dialog';
import usuarioService, { type Usuario, type UsuarioInput, type PerfilModulo } from '@/services/userAdmin';
import empresaService, { type Empresa } from '@/services/empresaAdmin';

const FORM_VAZIO: UsuarioInput = {
  username: '',
  email: '',
  first_name: '',
  last_name: '',
  company_id: '',
  phone: '',
  cpf: '',
  cargo: '',
  departamento: '',
  is_admin: false,
  is_company_admin: false,
  is_manager: false,
  is_supervisor: false,
  is_operator: false,
  is_active: true,
  sped_perfil: 'BLOQUEADO',
  dfe_perfil: 'BLOQUEADO',
  password: '',
  password_confirm: '',
};

const SPED_OPTIONS: { value: PerfilModulo; label: string }[] = [
  { value: 'BLOQUEADO', label: 'Bloqueado' },
  { value: 'CONSULTA', label: 'Consulta' },
  { value: 'COMPLETO', label: 'Completo' },
];

const DFE_OPTIONS: { value: PerfilModulo; label: string }[] = [
  { value: 'BLOQUEADO', label: 'Bloqueado' },
  { value: 'CONSULTA', label: 'Consulta' },
  { value: 'EMISSAO', label: 'Emissão' },
  { value: 'COMPLETO', label: 'Completo' },
];

export default function AdminUsuarios() {
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [busca, setBusca] = useState('');

  const [dialog, setDialog] = useState(false);
  const [dialogResetSenha, setDialogResetSenha] = useState(false);

  const [form, setForm] = useState<UsuarioInput & { id?: string }>(FORM_VAZIO);
  const [itemResetSenha, setItemResetSenha] = useState<Usuario | null>(null);
  const [novaSenha, setNovaSenha] = useState('');

  const carregar = async () => {
    setLoading(true);
    try {
      const [dataUsuarios, dataEmpresas] = await Promise.all([usuarioService.listar(), empresaService.listar()]);
      setUsuarios(dataUsuarios);
      setEmpresas(dataEmpresas);
    } catch (error) {
      console.error('Erro ao carregar usuários', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const getEmpresaNome = (item: Usuario) => {
    if (!item.company_id) return '-';
    const empresa = empresas.find((e) => e.id === item.company_id);
    return empresa ? (empresa.trade_name || empresa.social_name) : '-';
  };

  const filtrados = useMemo(() => {
    if (!busca) return usuarios;
    const termo = busca.toLowerCase();
    return usuarios.filter(
      (u) =>
        u.username?.toLowerCase().includes(termo) ||
        u.email?.toLowerCase().includes(termo) ||
        u.first_name?.toLowerCase().includes(termo) ||
        u.last_name?.toLowerCase().includes(termo)
    );
  }, [usuarios, busca]);

  const abrirNovo = () => {
    setForm(FORM_VAZIO);
    setDialog(true);
  };

  const editar = (item: Usuario) => {
    setForm({ ...item, password: '', password_confirm: '' });
    setDialog(true);
  };

  const salvar = async () => {
    if (!form.username || !form.email) return;
    if (!form.id && form.password !== form.password_confirm) {
      alert('As senhas não coincidem');
      return;
    }
    setSalvando(true);
    try {
      if (form.id) {
        const { password, password_confirm, ...rest } = form;
        await usuarioService.atualizar(form.id, rest);
      } else {
        await usuarioService.criar(form);
      }
      setDialog(false);
      await carregar();
    } catch (error) {
      console.error('Erro ao salvar usuário', error);
    } finally {
      setSalvando(false);
    }
  };

  const deletar = async (item: Usuario) => {
    try {
      await usuarioService.excluir(item.id);
      await carregar();
    } catch (error) {
      console.error('Erro ao excluir usuário', error);
      throw error;
    }
  };

  const abrirResetSenha = (item: Usuario) => {
    setItemResetSenha(item);
    setNovaSenha('Mudar@123');
    setDialogResetSenha(true);
  };

  const confirmarResetSenha = async () => {
    if (!itemResetSenha) return;
    try {
      await usuarioService.resetarSenha(itemResetSenha.id, novaSenha);
      setDialogResetSenha(false);
    } catch (error) {
      console.error('Erro ao resetar senha', error);
    }
  };

  return (
    <div className="nx-page">
      <PageHeader
        icon={<Users size={21} strokeWidth={2} />}
        title="Usuários"
        description="Gestão de usuários e permissões de acesso ao sistema."
        actions={
          <Button type="button" onClick={() => navigate('/admin/usuarios/novo')}>
            <Plus size={16} strokeWidth={2.2} />
            Novo Usuário
          </Button>
        }
      />

      <Card className="nx-page-card nx-sngpc-listing-card">
        <CardHead
          title="Listagem"
          actions={
            <div className="nx-card-head-actions">
              <div className="nx-search-field">
                <Search size={15} strokeWidth={2} aria-hidden />
                <input
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar usuário..."
                />
              </div>
            </div>
          }
        />

        {!loading && filtrados.length === 0 ? (
          <EmptyState message="Nenhum usuário encontrado." />
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>Empresa</th>
                  <th>Cargo</th>
                  <th>Admin</th>
                  <th>Status</th>
                  <th className="nx-col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="nx-table-loading">Carregando…</td>
                  </tr>
                ) : (
                  filtrados.map((item) => (
                    <tr key={item.id}>
                      <td>{item.first_name} {item.last_name}</td>
                      <td>{item.email}</td>
                      <td>{getEmpresaNome(item)}</td>
                      <td>{item.cargo || '-'}</td>
                      <td>
                        {item.is_admin ? <StatusBadge label="Admin" tone="info" /> : '-'}
                      </td>
                      <td>
                        <StatusBadge label={item.is_active ? 'Ativo' : 'Inativo'} tone={item.is_active ? 'success' : 'danger'} />
                      </td>
                      <td className="nx-col-acoes">
                        <div className="nx-table-actions-group nx-table-actions-group--buttons">
                          <IconButton label="Resetar senha" onClick={() => abrirResetSenha(item)}>
                            <KeyRound size={15} strokeWidth={1.9} aria-hidden />
                          </IconButton>
                          <TableActions
                            onEdit={() => navigate(`/admin/usuarios/${item.id}/editar`)}
                            onDelete={() => deletar(item)}
                            deleteTitle="Excluir usuário"
                            deleteDescription={`Tem certeza que deseja excluir o usuário "${item.username}"? Esta ação não pode ser desfeita.`}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={dialog} onOpenChange={(next) => !salvando && setDialog(next)}>
        <DialogContent className="nexus-dialog--wide nx-sngpc-edit-dialog">
          <form onSubmit={(event) => { event.preventDefault(); void salvar(); }}>
          <DialogHeader>
            <DialogTitle>{form.id ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
            <DialogDescription>
              {form.id ? 'Atualize os dados cadastrais e as permissões do usuário.' : 'Preencha os dados para incluir um novo usuário.'}
            </DialogDescription>
          </DialogHeader>

          <div className="nexus-dialog-body">
            <div className="nx-form-grid">
              <Field label="Usuário *" htmlFor="usuario-username">
                <Input
                  id="usuario-username"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  required
                />
              </Field>
              <Field label="E-mail *" htmlFor="usuario-email">
                <Input
                  id="usuario-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </Field>
              <Field label="Nome" htmlFor="usuario-first-name">
                <Input
                  id="usuario-first-name"
                  value={form.first_name || ''}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                />
              </Field>
              <Field label="Sobrenome" htmlFor="usuario-last-name">
                <Input
                  id="usuario-last-name"
                  value={form.last_name || ''}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                />
              </Field>
              <Field label="Empresa" htmlFor="usuario-empresa">
                <Select
                  id="usuario-empresa"
                  value={form.company_id || ''}
                  onChange={(e) => setForm({ ...form, company_id: e.target.value })}
                >
                  <option value="">Nenhuma</option>
                  {empresas.map((e) => (
                    <option key={e.id} value={e.id}>{e.trade_name || e.social_name}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Telefone" htmlFor="usuario-telefone">
                <Input
                  id="usuario-telefone"
                  value={form.phone || ''}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </Field>
              <Field label="CPF" htmlFor="usuario-cpf">
                <Input
                  id="usuario-cpf"
                  value={form.cpf || ''}
                  onChange={(e) => setForm({ ...form, cpf: e.target.value })}
                />
              </Field>
              <Field label="Cargo" htmlFor="usuario-cargo">
                <Input
                  id="usuario-cargo"
                  value={form.cargo || ''}
                  onChange={(e) => setForm({ ...form, cargo: e.target.value })}
                />
              </Field>
              <Field label="Departamento" htmlFor="usuario-departamento">
                <Input
                  id="usuario-departamento"
                  value={form.departamento || ''}
                  onChange={(e) => setForm({ ...form, departamento: e.target.value })}
                />
              </Field>
              <Field label="Perfil SPED" htmlFor="usuario-sped-perfil">
                <Select
                  id="usuario-sped-perfil"
                  value={form.sped_perfil || 'BLOQUEADO'}
                  onChange={(e) => setForm({ ...form, sped_perfil: e.target.value as PerfilModulo })}
                >
                  {SPED_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Perfil DF-e" htmlFor="usuario-dfe-perfil">
                <Select
                  id="usuario-dfe-perfil"
                  value={form.dfe_perfil || 'BLOQUEADO'}
                  onChange={(e) => setForm({ ...form, dfe_perfil: e.target.value as PerfilModulo })}
                >
                  {DFE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Select>
              </Field>

              {!form.id && (
                <>
                  <Field label="Senha *" htmlFor="usuario-senha">
                    <Input
                      id="usuario-senha"
                      type="password"
                      value={form.password || ''}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      required
                    />
                  </Field>
                  <Field label="Confirmar senha *" htmlFor="usuario-senha-confirmar">
                    <Input
                      id="usuario-senha-confirmar"
                      type="password"
                      value={form.password_confirm || ''}
                      onChange={(e) => setForm({ ...form, password_confirm: e.target.value })}
                      required
                    />
                  </Field>
                </>
              )}

              <label className="nx-sngpc-checkbox-field">
                <Checkbox
                  checked={!!form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                <span>Ativo</span>
              </label>
              <label className="nx-sngpc-checkbox-field">
                <Checkbox
                  checked={!!form.is_admin}
                  onChange={(e) => setForm({ ...form, is_admin: e.target.checked })}
                />
                <span>Administrador</span>
              </label>
              <label className="nx-sngpc-checkbox-field">
                <Checkbox
                  checked={!!form.is_company_admin}
                  onChange={(e) => setForm({ ...form, is_company_admin: e.target.checked })}
                />
                <span>Admin da Empresa</span>
              </label>
              <label className="nx-sngpc-checkbox-field">
                <Checkbox
                  checked={!!form.is_manager}
                  onChange={(e) => setForm({ ...form, is_manager: e.target.checked })}
                />
                <span>Gerente</span>
              </label>
              <label className="nx-sngpc-checkbox-field">
                <Checkbox
                  checked={!!form.is_supervisor}
                  onChange={(e) => setForm({ ...form, is_supervisor: e.target.checked })}
                />
                <span>Supervisor</span>
              </label>
              <label className="nx-sngpc-checkbox-field">
                <Checkbox
                  checked={!!form.is_operator}
                  onChange={(e) => setForm({ ...form, is_operator: e.target.checked })}
                />
                <span>Operador</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={salvando}>Cancelar</Button>
            </DialogClose>
            <Button type="submit" loading={salvando} loadingText="Salvando...">
              <Save size={16} /> Salvar
            </Button>
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogResetSenha} onOpenChange={setDialogResetSenha}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resetar Senha</DialogTitle>
            <DialogDescription>
              Nova senha para <strong>{itemResetSenha?.username}</strong>:
            </DialogDescription>
          </DialogHeader>
          <Field label="Nova senha" htmlFor="usuario-nova-senha">
            <Input
              id="usuario-nova-senha"
              value={novaSenha}
              onChange={(e) => setNovaSenha(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">Cancelar</Button>
            </DialogClose>
            <Button type="button" onClick={confirmarResetSenha}>Resetar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
